import { gqlClient } from '@/api/graphql-client';
import { getNextPageParamFromPaginatedResult } from '@/api/infiniteQueries';
import meKeys from '@/api/queries/me/keyFactory';
import { graphql } from '@/gql';
import {
  GetDocumentTemplatesInput,
  MyAccountDocumentTemplatesQuery,
  PaginationInput,
} from '@/gql/graphql';
import { PAGINATION_ARG_DEFAULTS } from '@payaca/shared-isomorphic/constants/graphql-pagination';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

const GET_MY_ACCOUNT_DOCUMENT_TEMPLATES = graphql(`
  query MyAccountDocumentTemplates(
    $pagination: PaginationInput
    $input: GetDocumentTemplatesInput
  ) {
    me {
      user {
        account {
          documentTemplates(pagination: $pagination, input: $input) {
            limit
            offset
            totalCount
            items {
              id
              name
              description
            }
          }
        }
      }
    }
  }
`);

export type DocumentTemplate = NonNullable<
  MyAccountDocumentTemplatesQuery['me']['user']['account']['documentTemplates']
>['items'][0];

const useGetMyAccountDocumentTemplates = (args?: {
  input?: GetDocumentTemplatesInput;
  pagination?: PaginationInput;
}) => {
  const { pagination, input } = args || {};

  const { data, ...rest } = useQuery({
    queryKey: meKeys.accountDocumentTemplates(input, pagination),
    queryFn: () => {
      return gqlClient.request(GET_MY_ACCOUNT_DOCUMENT_TEMPLATES, {
        pagination: {
          limit: pagination?.limit || PAGINATION_ARG_DEFAULTS.limit,
          offset: pagination?.offset || PAGINATION_ARG_DEFAULTS.offset,
        },
        input,
      });
    },
  });

  return { data: data?.me.user.account.documentTemplates, ...rest };
};

export const useInfiniteGetMyAccountDocumentTemplates = (args?: {
  input?: GetDocumentTemplatesInput;
}) => {
  const { input } = args || {};

  const queryFn = ({ pageParam }: { pageParam: number }) => {
    return gqlClient.request(GET_MY_ACCOUNT_DOCUMENT_TEMPLATES, {
      pagination: {
        offset: pageParam,
        limit: PAGINATION_ARG_DEFAULTS.limit,
      },
      input,
    });
  };

  const { data, ...rest } = useInfiniteQuery({
    queryKey: meKeys.accountDocumentTemplates(input),
    queryFn,
    initialPageParam: PAGINATION_ARG_DEFAULTS.offset,
    getNextPageParam: getNextPageParamFromPaginatedResult(
      (lastPage) => lastPage.me.user.account.documentTemplates
    ),
  });

  return {
    documentTemplates: data?.pages
      .flatMap((page) => page.me.user.account.documentTemplates?.items)
      .filter((i) => !!i),
    totalCount:
      data?.pages[0]?.me.user.account.documentTemplates?.totalCount || 0,
    ...rest,
  };
};

export default useGetMyAccountDocumentTemplates;
